import { ArrowRight, ShieldCheck } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { motion, useReducedMotion } from "motion/react";
import { Card, Wordmark, buttonVariants, cn, springs } from "@samklang/ui";

export function WelcomeScreen() {
  const reducedMotion = useReducedMotion();

  return (
    <section className="mx-auto grid min-h-[calc(100dvh-180px)] w-full max-w-6xl items-center gap-10 px-5 py-8 sm:px-8 lg:grid-cols-[1.05fr_0.95fr]">
      <motion.div
        initial={reducedMotion ? false : { opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={springs.soft}
        className="space-y-7"
      >
        <Wordmark className="text-foreground" />
        <div className="space-y-4">
          <p className="text-sm font-semibold uppercase tracking-wider text-primary">Anpassat samtalsstöd för skola och elevhälsan</p>
          <h1 className="max-w-2xl font-display text-4xl font-semibold leading-tight tracking-tight sm:text-6xl">Flera perspektiv. En tydligare bild.</h1>
          <p className="max-w-xl text-lg leading-relaxed text-foreground-muted sm:text-xl">Samklang är ett samtalsstöd utformat för alla elever, med NPF-säkrad struktur och ett tydligt elevperspektiv. Med Samklang samlar du enkelt elevens, skolans och vårdnadshavarens perspektiv på skolsituationen – och får en samlad bild som blir ett tvärprofessionellt underlag för exempelvis utredningar om särskilt stöd, skolsociala utredningar, frånvaroutredningar och socialpedagogiska bedömningar.</p>
          <p className="font-display text-xl font-semibold text-primary">Barnets röst i centrum. Hela bilden runt omkring.</p>
        </div>
        <div className="flex flex-wrap gap-3"><a href="/login?mode=request" className={cn(buttonVariants({ fullWidth: false }), "min-w-52")}>Anmäl intresse <ArrowRight aria-hidden="true" size={20} /></a><Link to="/login" className={cn(buttonVariants({ variant: "secondary", fullWidth: false }), "min-w-40")}>Logga in</Link></div>
        <div className="flex items-start gap-3 text-sm text-foreground-muted">
          <ShieldCheck aria-hidden="true" className="mt-0.5 shrink-0 text-secondary" size={20} />
          <p>NPF-säkrat för alla barn och unga. Barnet behöver aldrig ett eget konto.</p>
        </div>
      </motion.div>

      <div aria-hidden="true" className="relative mx-auto w-full max-w-lg">
        <div className="absolute -left-10 top-4 h-44 w-44 rounded-full bg-secondary-soft blur-3xl" />
        <div className="absolute -right-4 bottom-0 h-52 w-52 rounded-full bg-primary-soft blur-3xl" />
        <Card padding="lg" elevation="floating" className="relative overflow-hidden">
          <div className="space-y-6 py-6 text-center">
            <p className="font-display text-2xl font-semibold">Hur känns skolan för dig just nu?</p>
            <div className="mx-auto flex max-w-sm items-end justify-center gap-2 sm:gap-3">
              {[["😞", "1", "Mycket jobbigt"], ["🙁", "2", "Jobbigt"], ["😐", "3", "Okej"], ["🙂", "4", "Bra"], ["😊", "5", "Mycket bra"]].map(([symbol, number, label]) => (
                <div key={number} className="flex flex-col items-center gap-1 rounded-2xl border border-border bg-surface-raised px-2 py-3 sm:px-3">
                  <span aria-hidden="true" className="text-2xl sm:text-3xl">{symbol}</span>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-soft text-sm font-bold text-primary">{number}</span>
                  <span className="text-xs text-foreground-muted">{label}</span>
                </div>
              ))}
            </div>
            <p className="text-foreground-muted">Så här fungerar ett svar på en skala.</p>
          </div>
        </Card>
      </div>
    </section>
  );
}
